import { JsonAttributeType } from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { JsonAttributesByEntity } from "src/context/FoodpilotContext.tsx";
import z from "zod";

/**
 * Maps a JSON attribute type from the API to its corresponding Zod schema type.
 * Makes the value optional and nullable to handle cases where the attribute is not present.
 */
const getZodTypeFromAttribute = (type: JsonAttributeType): z.ZodTypeAny => {
    switch (type) {
        case "string":
            return z.string().optional().nullable();
        case "int":
        case "float":
            return z.number().optional().nullable();
        case "bool":
            return z.boolean().optional().nullable();
        default:
            return z.any().optional();
    }
};

/**
 * Dynamically extends a given Zod object schema with fields based on a list of JsonAttribute definitions.
 */
export const extendSchemaWithJsonAttributes = <T extends z.ZodObject<any>>(
    baseSchema: T,
    jsonAttributesByEntity: JsonAttributesByEntity | undefined,
    entityClass: string,
): T | z.ZodObject<any> => {
    const entityAttributes = jsonAttributesByEntity ? jsonAttributesByEntity[entityClass] : undefined;

    if (!entityAttributes || entityAttributes.length === 0) {
        return baseSchema;
    }

    const dynamicShape: Record<string, z.ZodTypeAny> = {};
    entityAttributes.forEach((attr) => {
        dynamicShape[attr.identifier] = getZodTypeFromAttribute(attr.type);
    });

    return baseSchema.extend(dynamicShape);
};
